import { Routes, Route, Navigate } from "react-router-dom";
import Login from "./pages/Login";
import VerifyEmail from "./pages/VerifyEmail";
import TotpSetup from "./pages/TotpSetup";
import TotpConfirm from "./pages/TotpConfirm";
import TotpVerify from "./pages/TotpVerify";
import Licences from "./pages/Licences";
import { AUTH_STORAGE_KEYS, getAuthValue, getAuthFlowStep } from "./api/authApi";

const useAuth = () => ({
  isLoggedIn: getAuthValue(AUTH_STORAGE_KEYS.hasSession) === "1",
  step: getAuthFlowStep(),
});

const RedirectTo = ({ to }) => <Navigate to={to} replace />;

function ProtectedRoute({ children }) {
  const { isLoggedIn } = useAuth();

  if (!isLoggedIn) {
    return <RedirectTo to="/login" />;
  }

  return children;
}

function PublicRoute({ children }) {
  const { isLoggedIn } = useAuth();

  if (isLoggedIn) {
    return <RedirectTo to="/licences" />;
  }

  return children;
}

function FlowRoute({ allowSteps, requireKeys = [], fallback = "/login", children }) {
  const { isLoggedIn, step } = useAuth();

  if (isLoggedIn) return <RedirectTo to="/licences" />;

  const hasRequiredKeys = requireKeys.every(key => getAuthValue(key));
  const isStepValid = !allowSteps.length || allowSteps.includes(step);

  if (!hasRequiredKeys || !isStepValid) {
    return <RedirectTo to={fallback} />;
  }

  return children;
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      <Route
        path="/verify-email"
        element={
          <FlowRoute
            allowSteps={["verify_email"]}
            requireKeys={[AUTH_STORAGE_KEYS.partnerId, AUTH_STORAGE_KEYS.challengeId]}
            fallback="/login"
          >
            <VerifyEmail />
          </FlowRoute>
        }
      />

      <Route
        path="/2fa/setup"
        element={
          <FlowRoute
            allowSteps={["totp_setup", "totp_confirm"]}
            requireKeys={[AUTH_STORAGE_KEYS.partnerId, AUTH_STORAGE_KEYS.setupToken]}
            fallback="/login"
          >
            <TotpSetup />
          </FlowRoute>
        }
      />

      <Route
        path="/2fa/confirm"
        element={
          <FlowRoute
            allowSteps={["totp_confirm"]}
            requireKeys={[AUTH_STORAGE_KEYS.partnerId, AUTH_STORAGE_KEYS.setupToken]}
            fallback="/2fa/setup"
          >
            <TotpConfirm />
          </FlowRoute>
        }
      />

      <Route
        path="/2fa/verify"
        element={
          <FlowRoute
            allowSteps={["totp_verify"]}
            requireKeys={[AUTH_STORAGE_KEYS.partnerId, AUTH_STORAGE_KEYS.tempToken]}
            fallback="/login"
          >
            <TotpVerify />
          </FlowRoute>
        }
      />

      <Route
        path="/licences"
        element={
          <ProtectedRoute>
            <Licences />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;